if (!customElements.get('mosaic-shopping')) {
  customElements.define('mosaic-shopping', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dataset.connected = 'true';
      this.content = this.querySelector('[data-shopping-content]');
      this.status = this.querySelector('[data-shopping-status]');
      this.hasScrolled = window.scrollY > 0;
      this.isVisible = false;
      this.started = false;
      this.expanded = false;
      this.onScroll = () => {
        this.hasScrolled = true;
        if (!this.observer) {
          const rect = this.getBoundingClientRect();
          this.isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        }
        this.startWhenReady();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.isVisible = entries.some(entry => entry.isIntersecting);
          this.startWhenReady();
        }, { rootMargin: '0px', threshold: 0.01 });
        this.observer.observe(this);
      }
      this.addEventListener('click', event => {
        const retry = event.target.closest('[data-shopping-retry]');
        if (retry) { this.load(this.lastUrl || this.initialUrl()); return; }
        const link = event.target.closest('a[data-shopping-link]');
        if (!link || !this.contains(link) || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        let url = new URL(link.href, window.location.origin);
        if (link.matches('[data-tm-sort]')) {
          url = new URL(this.lastUrl || this.initialUrl());
          url.searchParams.set('sort_by', url.searchParams.get('sort_by') === 'price-ascending' ? 'price-descending' : 'price-ascending');
          url.searchParams.delete('page');
        }
        if (url.origin !== window.location.origin) return;
        event.preventDefault();
        if (link.dataset.shoppingPage !== 'true') {
          this.expanded = true;
          url.searchParams.delete('page');
        }
        this.load(url, { focusKey: link.dataset.focusKey, page: link.dataset.shoppingPage === 'true' });
      });
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.observer?.disconnect();
      this.controller?.abort();
      this.querySelectorAll('.tm-md').forEach(root => root.tmDiscoveryDestroy?.());
    }
    initialUrl() {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      if (this.dataset.defaultColor) url.searchParams.set('filter.p.m.custom.glasmosaik_farbe', this.dataset.defaultColor);
      return url;
    }
    startWhenReady() {
      if (this.started || !this.hasScrolled || !this.isVisible) return;
      this.started = true;
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      this.load(this.initialUrl());
    }
    async load(target, options = {}) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const url = new URL(target, window.location.origin);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.expanded ? this.dataset.resultSection : (this.dataset.initialSection || this.dataset.resultSection));
      this.lastUrl = url;
      this.dataset.loadState = 'loading';
      this.dataset.requestCount = String(Number(this.dataset.requestCount || 0) + 1);
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingText;
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('response');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = doc.querySelector('[data-shopping-response]');
        if (!result) throw new Error('content');
        if (this.controller !== controller) return;
        this.querySelectorAll('.tm-md').forEach(root => root.tmDiscoveryDestroy?.());
        result.querySelectorAll('.tm-collections__link,.tm-color__link,.tm-reset,[data-tm-sort],.pagination a').forEach(link => {
          const clean = new URL(link.href, window.location.origin);
          clean.searchParams.delete('section_id');
          link.href = clean.pathname + clean.search;
          link.dataset.shoppingLink = 'true';
          if (link.closest('.pagination')) link.dataset.shoppingPage = 'true';
          else link.dataset.focusKey = link.title || link.textContent.trim();
        });
        result.querySelectorAll('.grid-product [data-aos],.grid-product[data-aos]').forEach(element => element.removeAttribute('data-aos'));
        this.content.replaceChildren(result);
        if (!window.TiamontMosaicDiscovery) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = this.dataset.discoveryScript;
            script.onload = resolve;
            script.onerror = reject;
            document.head.append(script);
          });
        }
        if (this.controller !== controller) return;
        window.TiamontMosaicDiscovery.init(this.content);
        this.dataset.loadState = 'ready';
        this.status.textContent = '';
        if (options.focusKey) {
          const focus = [...this.querySelectorAll('[data-focus-key]')].find(element => element.dataset.focusKey === options.focusKey);
          focus?.focus({ preventScroll: true });
        }
        if (options.page) {
          const heading = this.querySelector('[data-result-count]');
          heading?.focus({ preventScroll: true });
          heading?.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.dataset.loadState = 'error';
        this.status.textContent = this.dataset.errorText;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tmshop-retry';
        button.dataset.shoppingRetry = 'true';
        button.textContent = this.dataset.retryText;
        this.content.replaceChildren(button);
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }
  });
}
